import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ModalDialog } from 'components/ModalDialog';
import { BimPriceLevelBadge } from 'pages/Project/BimProject/BimCommonComponents/BimPriceLevelChoice';
import { cnMixSpace, Select, Text } from 'uikit';

import { useCallback, useMemo, useState } from 'react';

import { stylesRules } from './cellRenderer';
import { useAnalyticsData, useBaseAnalyticsData, useTableColumns, useVersionItems } from './hooks';
import type { IBimWorkOrderPriceAnalytics } from './types';

import Styles from './analytics.module.css';

export const BimWorkOrderPriceAnalytics = ({ onClose, baseRow, priceLevel }: IBimWorkOrderPriceAnalytics) => {
    const [selectedVersion, setSelectedVersion] = useState<(typeof versionItems)[0]>();

    const { isLoading, data: versionItems } = useVersionItems({
        workOrderPriceId: baseRow?.data?.id,
    });

    const versionSelectHandler = useCallback(({ value }) => {
        setSelectedVersion(value);
    }, []);

    const { itemsStatusBlock, baseData } = useBaseAnalyticsData({
        workOrderPriceId: baseRow?.data?.id,
        refWorkOrderPriceId: selectedVersion?.id,
        priceLevel,
    });
    const { tableData, changeFiltersHandler } = useAnalyticsData({
        workOrderPriceId: baseRow?.data?.id,
        refWorkOrderPriceId: selectedVersion?.id,
        priceLevel,
    });
    const columns = useTableColumns({
        workOrderPriceId: baseRow?.data?.id,
        refWorkOrderPriceId: selectedVersion?.id,
        changeFiltersHandler,
    });

    const baseRowCode = baseRow?.data?.planValue?.code ?? '';

    const title = (
        <div className={Styles.modalTitle}>
            <div className={Styles.titleBlock}>
                {baseRowCode && (
                    <Text view="secondary">
                        Сравнение{' '}
                        <Text as="span" weight="bold">
                            {baseRowCode}
                        </Text>{' '}
                        и
                    </Text>
                )}

                {versionItems && (
                    <Select
                        items={versionItems}
                        value={selectedVersion}
                        onChange={versionSelectHandler}
                        isLoading={isLoading}
                        placeholder="Выберите версию"
                        labelPosition="left"
                        size="s"
                        getItemLabel={(item) => item?.label ?? ''}
                        className={Styles.versionSelect}
                    />
                )}
                <BimPriceLevelBadge
                    priceLevel={priceLevel}
                    size="l"
                    view="filled"
                    className={cnMixSpace({ mL: 's' })}
                />
            </div>
            <div className={Styles.statusBlock}>{itemsStatusBlock}</div>
        </div>
    );

    const noRowsOverlay = useMemo(
        () => (selectedVersion ? 'Нет данных' : 'Для отображения данных выберите версию для сравнения'),
        [selectedVersion],
    );

    return (
        <ModalDialog
            isOpen
            title={title}
            onClose={onClose}
            withBottomButtons={false}
            isOnClickOutsideDisable
            dockedToWindow="bottom"
            withTitleCloseButton
        >
            <div
                className={`ag-theme-alpine customHeaderColor BimWorkOrderPriceDetails ${cnMixSpace({
                    pV: 's',
                    pH: 'l',
                })}`}
            >
                <AgGridTable
                    className={Styles.analyticsTable}
                    columnDefs={columns}
                    pinnedTopRowData={baseData}
                    rowData={tableData}
                    getRowHeight={() => 80}
                    rowClassRules={stylesRules}
                    animateRows
                    overlayNoRowsTemplate={noRowsOverlay}
                />
            </div>
        </ModalDialog>
    );
};

BimWorkOrderPriceAnalytics.displayName = 'BimWorkOrderPriceAnalytics';
